<template>
  <div class="financial-stats-dashboard">
    <!-- Header Section -->
    <div class="page-header mb-1">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="page-title mb-2">{{ $t('financialStats.title') }}</h1>
          <p class="text-muted">{{ $t('financialStats.subtitle') }}</p>
        </div>
        <div class="d-flex align-items-center">
          <a-range-picker
            v-model="dateRange"
            :placeholder="[$t('financialStats.startDate'), $t('financialStats.endDate')]"
            @change="onDateRangeChange"
            class="mr-3"
          />
          <a-button type="primary" @click="refreshData" :loading="loading">
            <a-icon type="reload" />
            {{ $t('financialStats.refresh') }}
          </a-button>
        </div>
      </div>
    </div>

    <!-- KPI Cards Row -->
    <div class="row mb-1">
      <div class="col-xl-3 col-lg-6 col-md-6 mb-1">
        <kpi-card
          :loading="loading"
          :value="financialOverview.totalRevenue"
          :title="$t('financialStats.totalRevenue')"
          :subtitle="$t('financialStats.revenueSubtitle')"
          color="#52c41a"
          icon="dollar"
          :trend="revenueTrend"
        />
      </div>
      <div class="col-xl-3 col-lg-6 col-md-6 mb-1">
        <kpi-card
          :loading="loading"
          :value="financialOverview.totalExpenses"
          :title="$t('financialStats.totalExpenses')"
          :subtitle="$t('financialStats.expensesSubtitle')"
          color="#ff4d4f"
          icon="minus"
          :trend="expenseTrend"
        />
      </div>
      <div class="col-xl-3 col-lg-6 col-md-6 mb-1">
        <kpi-card
          :loading="loading"
          :value="financialOverview.netProfit"
          :title="$t('financialStats.netProfit')"
          :subtitle="$t('financialStats.profitSubtitle')"
          :color="financialOverview.netProfit >= 0 ? '#1890ff' : '#ff4d4f'"
          icon="arrow-up"
          :trend="profitTrend"
        />
      </div>
      <div class="col-xl-3 col-lg-6 col-md-6 mb-1">
        <kpi-card
          :loading="loading"
          :value="financialOverview.profitMargin + '%'"
          :title="$t('financialStats.profitMargin')"
          :subtitle="$t('financialStats.marginSubtitle')"
          :color="getMarginColor(financialOverview.profitMargin)"
          icon="percentage"
          :trend="marginTrend"
        />
      </div>
    </div>

    <!-- Comp V3: revenue mix, décaissement modes, cash movement -->
    <div class="row mb-1">
      <div class="col-xl-4 col-lg-6 mb-1">
        <div class="card h-100">
          <div class="card-body">
            <source-breakdown-chart
              :loading="loading"
              :data="chartData.revenueBreakdownPie"
              :total-amount="financialOverview.totalRevenue"
            />
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-lg-6 mb-1">
        <div class="card h-100">
          <div class="card-body">
            <payment-methods-chart
              :loading="loading"
              :data="chartData.depenseOutflowPie"
              :total-revenue="financialOverview.depensePaidOut || 0"
              title-key="financialStats.depenseOutflowTitle"
              subtitle-key="financialStats.depenseOutflowSubtitle"
            />
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-lg-12 mb-1">
        <div class="card h-100">
          <div class="card-header py-2">
            <h5 class="mb-0">{{ $t("financialStats.moneyFlowTitle") }}</h5>
          </div>
          <div class="card-body">
            <div class="d-flex flex-wrap justify-content-between align-items-start">
              <div class="mb-3 mr-3">
                <div class="text-muted small">{{ $t("financialStats.moneyIn") }}</div>
                <div class="h4 mb-0 text-success">{{ formatMoney(chartData.moneyFlowSummary && chartData.moneyFlowSummary.inflow) }}</div>
              </div>
              <div class="mb-3 mr-3">
                <div class="text-muted small">{{ $t("financialStats.moneyOut") }}</div>
                <div class="h4 mb-0 text-danger">{{ formatMoney(chartData.moneyFlowSummary && chartData.moneyFlowSummary.outflow) }}</div>
              </div>
              <div class="mb-3">
                <div class="text-muted small">{{ $t("financialStats.netCashPosition") }}</div>
                <div
                  class="h4 mb-0"
                  :class="netCashTone"
                >
                  {{ formatMoney(netCashPosition) }}
                </div>
              </div>
            </div>
            <a-divider class="my-2" />
            <div class="row text-center small text-muted">
              <div class="col-4">{{ $t("financialStats.tuitionShort") }}</div>
              <div class="col-4">{{ $t("financialStats.inverseShort") }}</div>
              <div class="col-4">{{ $t("financialStats.avoirShort") }}</div>
            </div>
            <div class="row text-center font-weight-bold">
              <div class="col-4">{{ formatMoney(financialOverview.tuitionRevenue) }}</div>
              <div class="col-4">{{ formatMoney(financialOverview.inverseEncaissementRevenue) }}</div>
              <div class="col-4">{{ formatMoney(financialOverview.avoirFournisseurRevenue) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <depense-type-bar-chart
              :loading="loading"
              :items="chartData.depenseByType"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Financial Health Status -->
    <div class="row mb-1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <a-avatar :size="64" :style="{ backgroundColor: getHealthColor() }">
                  <a-icon :type="getHealthIcon()" style="font-size: 24px;" />
                </a-avatar>
              </div>
              <div class="flex-grow-1">
                <h4 class="mb-1">{{ $t('financialStats.financialHealth') }}</h4>
                <p class="text-muted mb-2">{{ getHealthDescription() }}</p>
                <a-tag :color="getHealthColor()" class="mb-0">
                  {{ $t(`financialStats.health.${kpis.financialHealth}`) }}
                </a-tag>
              </div>
              <div class="text-right">
                <div class="font-size-24 font-weight-bold text-dark">
                  {{ financialOverview.totalInvoices }}
                </div>
                <div class="text-muted">{{ $t('financialStats.totalInvoices') }}</div>
                <div class="font-size-14 text-muted mt-1">
                  {{ $t('financialStats.cancellationRate') }}: {{ financialOverview.cancellationRate }}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row mb-1">
      <!-- Monthly Trend Chart -->
      <div class="col-xl-8 col-lg-12 mb-1">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('financialStats.monthlyTrend') }}</h5>
          </div>
          <div class="card-body">
            <monthly-trend-chart
              :loading="loading"
              :data="chartData.monthlyTrend"
            />
          </div>
        </div>
      </div>

      <!-- Payment Methods Distribution -->
      <div class="col-xl-4 col-lg-12 mb-1">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('financialStats.paymentMethods') }}</h5>
          </div>
          <div class="card-body">
            <payment-methods-chart
              :loading="loading"
              :data="chartData.paymentMethodPie"
              :total-revenue="financialOverview.totalRevenue"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Revenue vs Expenses Comparison -->
    <div class="row mb-1">
      <div class="col-xl-6 col-lg-12 mb-1">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('financialStats.revenueExpenseComparison') }}</h5>
          </div>
          <div class="card-body">
            <revenue-expense-chart
              :loading="loading"
              :data="chartData.revenueExpenseComparison"
            />
          </div>
        </div>
      </div>

      <!-- Top Performing Months -->
      <div class="col-xl-6 col-lg-12 mb-1">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('financialStats.topPerformingMonths') }}</h5>
          </div>
          <div class="card-body">
            <top-months-list
              :loading="loading"
              :data="kpis.topPerformingMonths"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Analytics Tabs -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <a-tabs v-model="activeTab" @change="onTabChange">
              <a-tab-pane key="revenue" :tab="$t('financialStats.revenueAnalytics')">
                <revenue-analytics
                  :loading="loadingRevenue"
                  :data="revenueData"
                />
              </a-tab-pane>
              <a-tab-pane key="expenses" :tab="$t('financialStats.expenseAnalytics')">
                <expense-analytics
                  :loading="loadingExpenses"
                  :data="expensesData"
                  :month-mapping="monthMapping"
                />
              </a-tab-pane>
              <a-tab-pane key="students" :tab="$t('financialStats.studentAnalytics')">
                <student-analytics
                  :loading="loadingStudents"
                  :data="studentsData"
                />
              </a-tab-pane>
              <a-tab-pane key="caisses" :tab="$t('financialStats.caisseAnalytics')">
                <caisse-analytics
                  :loading="loadingCaisses"
                  :data="caissesData"
                  :month-mapping="monthMapping"
                />
              </a-tab-pane>
            </a-tabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueChartist from "v-chartist";
import ChartistTooltip from "chartist-plugin-tooltips-updated";
import apiClient from "@/services/axios";
import moment from "moment";

// Import components
import KpiCard from "./components/KpiCard";
import MonthlyTrendChart from "./components/MonthlyTrendChart";
import PaymentMethodsChart from "./components/PaymentMethodsChart";
import SourceBreakdownChart from "./components/SourceBreakdownChart";
import DepenseTypeBarChart from "./components/DepenseTypeBarChart";
import RevenueExpenseChart from "./components/RevenueExpenseChart";
import TopMonthsList from "./components/TopMonthsList";
import RevenueAnalytics from "./components/RevenueAnalytics";
import ExpenseAnalytics from "./components/ExpenseAnalytics";
import StudentAnalytics from "./components/StudentAnalytics";
import CaisseAnalytics from "./components/CaisseAnalytics";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "FinancialStatsDashboard",
  components: {
    KpiCard,
    MonthlyTrendChart,
    PaymentMethodsChart,
    SourceBreakdownChart,
    DepenseTypeBarChart,
    RevenueExpenseChart,
    TopMonthsList,
    RevenueAnalytics,
    ExpenseAnalytics,
    StudentAnalytics,
    CaisseAnalytics,
  },
  data() {
    return {
      loading: true,
      loadingRevenue: false,
      loadingExpenses: false,
      loadingStudents: false,
      loadingCaisses: false,
      activeTab: "revenue",
      dateRange: null,
      
      // Main dashboard data
      financialOverview: {
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        profitMargin: 0,
        totalInvoices: 0,
        cancelledInvoices: 0,
        cancellationRate: 0,
        tuitionRevenue: 0,
        inverseEncaissementRevenue: 0,
        avoirFournisseurRevenue: 0,
        depensePaidOut: 0,
      },
      
      revenueAnalytics: {
        monthlyRevenue: Array(12).fill(0),
        revenueByPaymentMethod: {
          ESPESE: 0,
          CHEQUE: 0,
          TPE: 0,
          TRAITE: 0,
          VIREMENT: 0
        },
        revenueByType: {
          F: 0,
          R: 0
        },
        averageInvoiceValue: 0
      },
      
      expenseAnalytics: {
        monthlyExpenses: Array(12).fill(0),
        expensesByCategory: {
          employees: 0,
          general: 0,
          cancellations: 0
        },
        averageMonthlyExpense: 0
      },
      
      kpis: {
        monthlyProfit: Array(12).fill(0),
        cashFlow: Array(12).fill(0),
        paymentMethodDistribution: {},
        topPerformingMonths: [],
        financialHealth: "unknown"
      },
      
      chartData: {
        monthlyTrend: [],
        paymentMethodPie: [],
        revenueExpenseComparison: {},
        profitTrend: [],
        revenueBreakdownPie: [],
        depenseOutflowPie: [],
        depenseByType: [],
        moneyFlowSummary: { inflow: 0, outflow: 0 },
      },
      
      // Detailed analytics data
      revenueData: {},
      expensesData: {},
      studentsData: {},
      caissesData: {},
      
      // Month mapping for dynamic charts
      monthMapping: {
        months: [],
        labels: []
      },

      currency: null,
    };
  },

  computed: {
    netCashPosition() {
      const s = this.chartData.moneyFlowSummary || {};
      return Number((Number(s.inflow || 0) - Number(s.outflow || 0)).toFixed(2));
    },
    netCashTone() {
      return this.netCashPosition >= 0 ? "text-success" : "text-danger";
    },
    revenueTrend() {
      // Calculate trend based on recent months
      const recent = this.revenueAnalytics.monthlyRevenue.slice(-3);
      const previous = this.revenueAnalytics.monthlyRevenue.slice(-6, -3);
      const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
      const previousAvg = previous.reduce((a, b) => a + b, 0) / previous.length;
      return previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg * 100).toFixed(1) : 0;
    },
    
    expenseTrend() {
      const recent = this.expenseAnalytics.monthlyExpenses.slice(-3);
      const previous = this.expenseAnalytics.monthlyExpenses.slice(-6, -3);
      const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
      const previousAvg = previous.reduce((a, b) => a + b, 0) / previous.length;
      return previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg * 100).toFixed(1) : 0;
    },
    
    profitTrend() {
      const recent = this.kpis.monthlyProfit.slice(-3);
      const previous = this.kpis.monthlyProfit.slice(-6, -3);
      const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
      const previousAvg = previous.reduce((a, b) => a + b, 0) / previous.length;
      return previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg * 100).toFixed(1) : 0;
    },
    
    marginTrend() {
      return this.profitTrend; // Same as profit trend
    }
  },
  
  mounted() {
    this.currency = getBuildingCurrency();
    this.loadDashboardData();
    this.loadDetailedData(this.activeTab);
  },
  
  methods: {
    formatMoney(v) {
      const n = Number(v);
      const c = this.currency || "";
      if (v == null || Number.isNaN(n)) return `0 ${c}`.trim();
      return n.toLocaleString() + (c ? ` ${c}` : "");
    },
    async loadDashboardData() {
      this.loading = true;
      try {
        const params = {};
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = moment(this.dateRange[0]).format("YYYY-MM-DD");
          params.endDate = moment(this.dateRange[1]).format("YYYY-MM-DD");
        }
        
        const response = await apiClient.get("/statistiques/dashboard", { params });
        const data = response.data;
        
        this.financialOverview = data.financialOverview;
        this.revenueAnalytics = data.revenueAnalytics;
        this.expenseAnalytics = data.expenseAnalytics;
        this.kpis = data.kpis;
        this.chartData = data.chartData;
        this.monthMapping = data.monthMapping || { months: [], labels: [] };
        
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        this.$message.error(this.$t("error.failedToLoadFinancialStats"));
      } finally {
        this.loading = false;
      }
    },
    
    async loadDetailedData(tab) {
      const loadingKey = `loading${tab.charAt(0).toUpperCase() + tab.slice(1)}`;
      this[loadingKey] = true;
      
      try {
        let endpoint = "";
        let dataKey = `${tab}Data`;
        switch (tab) {
          case "revenue":
            endpoint = "/statistiques/revenue";
            break;
          case "expenses":
            endpoint = "/statistiques/expenses";
            break;
          case "students":
            endpoint = "/statistiques/students";
            break;
          case "caisses":
            endpoint = "/statistiques/caisses";
            break;
        }
        
        const params = {};
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = moment(this.dateRange[0]).format("YYYY-MM-DD");
          params.endDate = moment(this.dateRange[1]).format("YYYY-MM-DD");
        }
        
        const response = await apiClient.get(endpoint, { params });

        this[dataKey] = response.data;
        
      } catch (error) {
        console.error(`Error loading ${tab} data:`, error);
        const tabLabel = this.$t(`financialStats.${tab}Analytics`);
        this.$message.error(
          this.$t("error.failedToLoadAnalytics", { tab: tabLabel })
        );
      } finally {
        this[loadingKey] = false;
      }
    },
    
    onDateRangeChange() {
      this.loadDashboardData();
    },
    
    onTabChange(tab) {
      this.loadDetailedData(tab);
    },
    
    refreshData() {
      this.loadDashboardData();
      if (this.activeTab) {
        this.loadDetailedData(this.activeTab);
      }
    },
    
    getMarginColor(margin) {
      if (margin >= 20) return "#52c41a";
      if (margin >= 10) return "#1890ff";
      if (margin >= 5) return "#faad14";
      return "#ff4d4f";
    },
    
    getHealthColor() {
      const health = this.kpis.financialHealth;
      const colors = {
        excellent: "#52c41a",
        good: "#1890ff",
        fair: "#faad14",
        poor: "#ff4d4f",
        unknown: "#d9d9d9"
      };
      return colors[health] || colors.unknown;
    },
    
    getHealthIcon() {
      const health = this.kpis.financialHealth;
      const icons = {
        excellent: "smile",
        good: "smile",
        fair: "frown",
        poor: "frown",
        unknown: "question-circle"
      };
      return icons[health] || icons.unknown;
    },
    
    getHealthDescription() {
      const health = this.kpis.financialHealth;
      return this.$t(`financialStats.healthDescription.${health}`);
    },
    
    moment() {
      return moment;
    }
  }
};
</script>

<style lang="scss" scoped>
@import './styles/shared.scss';

.financial-stats-dashboard {
  .page-header {
    .page-title {
      font-size: 2rem;
      font-weight: 600;
      color: #262626;
    }
  }
}
</style>
